import {
    IonAlert,
    IonButton,
    IonCard,
    IonCardContent,
    IonIcon,
    useIonRouter,
} from "@ionic/react";
import { MatchPhone } from "refsix-js-models";
import { FC } from "react";
import { lockClosed, lockOpen } from "ionicons/icons";
import { IntegrationIcon } from "./IntegrationIcons";
import "./IntegrationMatchEditConnection.css";
import { useFormContext } from "react-hook-form";
import { updateMatch } from "../../services/matchService";
import { handleRefreshIntegrationMatches } from "../../services/integrations/integrationSyncUpdates";
import { useTranslation } from "react-i18next";
import {
    canDisconnectIntegrationMatch,
    integrationPartnerName,
} from "refsix-core";
import {
    trackDisconnectedIntegrationMatch,
    trackReconnectedIntegrationMatch,
} from "../../services/analytics/analyticsService";

interface IntegrationMatchEditConnectionProps {
    match: MatchPhone;
}
export const IntegrationMatchConnection: FC<
    IntegrationMatchEditConnectionProps
> = ({ match }) => {
    const { watch } = useFormContext();
    const integration = watch("integration");

    if (!integration || !canDisconnectIntegrationMatch(integration.partner)) {
        return null;
    }

    return integration.disconnected ? (
        <ReconnectIntegration match={match} />
    ) : (
        <DisconnectIntegration match={match} />
    );
};

const DisconnectIntegration: FC<IntegrationMatchEditConnectionProps> = ({
    match,
}) => {
    const { setValue, watch } = useFormContext();
    const integration = watch("integration");
    const { t } = useTranslation();
    const tOptions = { partner: integrationPartnerName(integration?.partner) };

    return (
        <IonCard className="integration-card">
            <IonCardContent className="integration-card-content">
                <div className="integration-card-header">
                    <div className="integration-card-header-logo">
                        <IntegrationIcon partner={integration?.partner} />{" "}
                        <span style={{ marginLeft: 5 }}>
                            {t(
                                "integrations.syncing.integrationMatch",
                                tOptions
                            )}
                        </span>
                    </div>
                    <IonButton
                        fill="clear"
                        color="warning"
                        size={"small"}
                        className="integration-card-header-button"
                        id={"integration-disconnect-button"}
                        data-testid="integration-disconnect-button"
                    >
                        {t("integrations.syncing.disconnect")}{" "}
                        <IonIcon src={lockOpen} />
                    </IonButton>
                    <IonAlert
                        trigger={"integration-disconnect-button"}
                        header={t("integrations.syncing.disconnect")}
                        message={t(
                            "integrations.syncing.disconnectingConfirmation",
                            tOptions
                        )}
                        buttons={[
                            {
                                text: t("general.cancel"),
                                role: "cancel",
                                cssClass: "secondary",
                            },
                            {
                                text: t("general.yes"),
                                handler: () => {
                                    setValue("integration.disconnected", true, {
                                        // The locks in MatchDetails /
                                        // MatchTeamDetails watch this value, so
                                        // without a re-render the referee taps
                                        // Disconnect and the fields stay grey.
                                        shouldDirty: true,
                                    });
                                    trackDisconnectedIntegrationMatch(match);
                                },
                            },
                        ]}
                    />
                </div>
                {t(
                    integration?.manualSync
                        ? "integrations.syncing.disconnectingManual"
                        : "integrations.syncing.disconnecting",
                    tOptions
                )}
            </IonCardContent>
        </IonCard>
    );
};
const ReconnectIntegration: FC<IntegrationMatchEditConnectionProps> = ({
    match,
}) => {
    const { watch } = useFormContext();
    const integration = watch("integration");
    const route = useIonRouter();
    const { t } = useTranslation();
    // integrationPartnerName, not capitalize: the latter renders
    // "Soccerrefmanager" and "Moas" rather than the partner's own name.
    const tOptions = { partner: integrationPartnerName(integration?.partner) };
    const manualSync = !!integration?.manualSync;

    return (
        <IonCard className="integration-card">
            <IonCardContent className="integration-card-content">
                <div className="integration-card-header">
                    <div className="integration-card-header-logo">
                        <IntegrationIcon partner={integration?.partner} />
                        <span style={{ marginLeft: 5 }}>
                            {t(
                                "integrations.syncing.integrationMatch",
                                tOptions
                            )}
                        </span>
                    </div>
                    <IonButton
                        fill="clear"
                        color="primary"
                        size={"small"}
                        className="integration-card-header-button"
                        id={"integration-disconnect-button"}
                        data-testid="integration-reconnect-button"
                    >
                        {t("integrations.syncing.reconnect")}{" "}
                        <IonIcon src={lockClosed} />
                    </IonButton>
                    <IonAlert
                        trigger={"integration-disconnect-button"}
                        header={t("integrations.syncing.reconnect")}
                        message={t(
                            manualSync
                                ? "integrations.syncing.reconnectingConfirmationManual"
                                : "integrations.syncing.reconnectingConfirmation",
                            tOptions
                        )}
                        buttons={[
                            {
                                text: t("general.cancel"),
                                role: "cancel",
                                cssClass: "secondary",
                            },
                            {
                                text: t("general.yes"),
                                handler: async () => {
                                    // Reconnecting the integration
                                    // Save the integration.disconnected state and refresh the match
                                    if (!match.integration) {
                                        return;
                                    }
                                    match.integration.disconnected = false;
                                    await updateMatch(match);
                                    // A manually-synced partner has no endpoint
                                    // to pull from — the referee's next import
                                    // is what restores the platform's version.
                                    if (!manualSync) {
                                        await handleRefreshIntegrationMatches();
                                    }
                                    trackReconnectedIntegrationMatch(match);
                                    route.goBack();
                                },
                            },
                        ]}
                    />
                </div>
                {t(
                    manualSync
                        ? "integrations.syncing.reconnectingManual"
                        : "integrations.syncing.reconnecting",
                    tOptions
                )}
            </IonCardContent>
        </IonCard>
    );
};
